import { Modal, Button, Stack } from "@mantine/core";
import { useForm } from "react-hook-form";
import { useEffect, useMemo } from "react";
import { TextInput, Textarea } from "react-hook-form-mantine";
import LocalizedInput from "../common/LocalizedInput";
import { usePatchSurvey } from "../../api/survey";
import resolver from "@congrs/resolver";
import { web_updateSurveySchema } from "@congrs/schemas";
import { extractDirtyData } from "../../utils/dirtyData";
import { mergeI18nWithDefaults } from "../../utils/i18nUtils";

function SurveyForm({ opened, onClose, survey, surveyId, event }) {
  const patchSurvey = usePatchSurvey(surveyId);

  const mainLanguage = event?.language || "en";
  const otherLanguages = useMemo(
    () => event?.secondaryLanguages || [],
    [event?.secondaryLanguages]
  );

  const defaultValues = {
    titleI18n: mergeI18nWithDefaults({}, mainLanguage, otherLanguages),
    descriptionI18n: mergeI18nWithDefaults({}, mainLanguage, otherLanguages),
  };

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty, isSubmitting, dirtyFields },
  } = useForm({
    defaultValues,
    resolver: resolver(web_updateSurveySchema),
  });

  useEffect(() => {
    if (survey) {
      reset({
        titleI18n: mergeI18nWithDefaults(
          survey.titleI18n,
          mainLanguage,
          otherLanguages
        ),
        descriptionI18n: mergeI18nWithDefaults(
          survey.descriptionI18n,
          mainLanguage,
          otherLanguages
        ),
      });
    }
  }, [survey, mainLanguage, otherLanguages, reset]);

  const onSubmit = async (data) => {
    const dirtyData = extractDirtyData(data, dirtyFields);

    if (Object.keys(dirtyData).length === 0) {
      onClose();
      return;
    }

    patchSurvey.mutate(dirtyData, {
      onSuccess: () => {
        reset(data);
        onClose();
      },
    });
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const localizedInputFields = [
    {
      nameBase: "titleI18n",
      label: "Title",
      component: TextInput,
      props: {
        size: "sm",
        placeholder: "Survey title",
        withAsterisk: true,
      },
    },
    {
      nameBase: "descriptionI18n",
      label: "Description",
      component: Textarea,
      props: {
        autosize: true,
        minRows: 4,
        maxRows: 8,
        size: "sm",
        placeholder: "Survey description",
      },
    },
  ];

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Edit Survey"
      size="lg"
      top
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap="md">
          <LocalizedInput
            control={control}
            fields={localizedInputFields}
            mainLanguage={mainLanguage}
            otherLanguages={otherLanguages}
          />

          <Button
            type="submit"
            fullWidth
            disabled={!isDirty}
            loading={isSubmitting}
          >
            Save Changes
          </Button>
        </Stack>
      </form>
    </Modal>
  );
}

export default SurveyForm;
